import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { normalizeValue } from "../../../utils/helpers";

import { AlertI } from "../../settings/common.types";
import { twofactorInitialState } from "./twofactor.types";
import { initialState } from "./twofactor.intial.state";
import { sendCode, validateCode } from "./twofactor.actions";
import { passwordlessLogin } from "../../login/slices/login.actions";

const initState: twofactorInitialState = initialState;

export const twofactorSlice = createSlice({
	name: "twofactor",
	initialState: initState,
	reducers: {
		triggerAlert: (state, action: PayloadAction<AlertI[]>) => {
			state.alerts.push(...action.payload);
		},
		onAlertClose: (state, action: PayloadAction<AlertI[]>) => {
			state.alerts = action.payload;
		},
		handleFieldValueChange: (
			state,
			action: PayloadAction<{ field: string; value: any }>,
		) => {
			state.form[action.payload.field] = normalizeValue(action.payload.value);
		},
	},
	extraReducers(builder) {
		builder.addCase(sendCode.pending, (state) => {
			state.isFetching = true;
			state.alerts = [];
		});

		builder.addCase(
			sendCode.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					data?: { exists: boolean };
					error?: string;
				}>,
			) => {
				state.isFetching = false;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						level: "error",
						messageKey: action.payload.error,
					});
				} else {
					state.alerts.push({
						level: "success",
						messageKey: "komid.smslogin.code.sent",
					});
				}
			},
		);

		builder.addCase(sendCode.rejected, (state) => {
			state.isFetching = false;
			state.alerts.push({
				level: "error",
				messageKey: "komid.general.network.error",
			});
		});

		builder.addCase(validateCode.pending, (state) => {
			state.isFetching = true;
			state.alerts = [];
		});

		builder.addCase(
			validateCode.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					error?: string;
				}>,
			) => {
				state.isFetching = false;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						level: "error",
						messageKey: action.payload.error,
					});
				}
			},
		);

		builder.addCase(validateCode.rejected, (state) => {
			state.isFetching = false;
			state.alerts.push({
				level: "error",
				messageKey: "komid.general.network.error",
			});
		});

		builder.addCase(passwordlessLogin.pending, (state) => {
			state.isFetchingPasswordlessLink = true;
			state.alerts = [];
		});

		builder.addCase(
			passwordlessLogin.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					errorCode?: string;
					errorLevel?: string;
					errorMessage?: string;
					messageValues?: {
						[key: string]: string;
					};
					error?: string;
				}>,
			) => {
				state.isFetchingPasswordlessLink = false;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						level: action.payload.errorLevel,
						messageKey: action.payload.error,
						messageValues: action.payload.messageValues,
					});
				} else {
					state.alerts.push({
						level: "success",
						messageKey: "komid.login.passwordlesslogin.successmessage",
					});
				}
			},
		);

		builder.addCase(passwordlessLogin.rejected, (state) => {
			state.isFetchingPasswordlessLink = false;
			state.alerts.push({
				level: "error",
				messageKey: "komid.general.network.error",
			});
		});
	},
});

export const { triggerAlert, onAlertClose, handleFieldValueChange } =
	twofactorSlice.actions;

export default twofactorSlice.reducer;
